import React, { useState, useEffect } from 'react';
import {
  X,
  Check,
  User,
  Mail,
  Lock,
  Shield,
  Phone,
  MapPin,
  Calendar,
  Briefcase,
  CreditCard,
  DollarSign,
  AlertOctagon,
  FileText,
  Key,
  Camera,
  Layers,
  UserSquare2
} from 'lucide-react';
import { UserData } from '../../types/User.types';
import { useToast } from '../../hooks/useToast';
import './EmployeeProfileModal.css';

export interface ExtendedUserData extends UserData {
  photoUrl?: string;
  position?: string;
  password?: string;
  dni?: string;
  phone?: string;
  birthDate?: string;
  address?: string;
  employeeId?: string;
  department?: string;
  hireDate?: string;
  hourlyCost?: number;
  iban?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  notes?: string;
}

interface EmployeeProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: ExtendedUserData | null;
  onSave: (updatedUser: ExtendedUserData) => void;
}

const PRESET_AVATARS = [
  'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&h=150&fit=crop&q=80',
  'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop&q=80',
  'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&q=80',
  'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&q=80',
  'https://images.unsplash.com/photo-1628157582853-a796fa650a6a?w=150&h=150&fit=crop&q=80',
  'https://images.unsplash.com/photo-1570295999919-56ceb5ecca61?w=150&h=150&fit=crop&q=80',
  'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=150&h=150&fit=crop&q=80',
  'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&q=80'
];

type ActiveTab = 'personal' | 'laboral' | 'financial' | 'notes';

export default function EmployeeProfileModal({
  isOpen,
  onClose,
  user,
  onSave
}: EmployeeProfileModalProps) {
  const { notify } = useToast();
  const [activeTab, setActiveTab] = useState<ActiveTab>('personal');
  const [showAvatarSelector, setShowAvatarSelector] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);

  // Form State
  const [formData, setFormData] = useState<Partial<ExtendedUserData>>({});
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Sync state with selected user
  useEffect(() => {
    if (user) {
      setFormData({ ...user });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordError('');
      setPasswordSuccess('');
      setShowPasswordChange(false);
      setShowAvatarSelector(false);
      setActiveTab('personal');
    }
  }, [user]);

  if (!isOpen || !user) return null;

  const handleInputChange = (field: keyof ExtendedUserData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    // Password change validation and API call if enabled
    if (showPasswordChange && (currentPassword || newPassword || confirmPassword)) {
      if (!currentPassword) {
        const msg = 'DEBE INGRESAR LA CONTRASEÑA ACTUAL';
        setPasswordError(msg);
        notify('Error al cambiar contraseña', msg, 'warning');
        return;
      }
      if (!newPassword) {
        const msg = 'DEBE INGRESAR LA NUEVA CONTRASEÑA';
        setPasswordError(msg);
        notify('Error al cambiar contraseña', msg, 'warning');
        return;
      }
      if (newPassword !== confirmPassword) {
        const msg = 'LAS CONTRASEÑAS NO COINCIDEN';
        setPasswordError(msg);
        notify('Error al cambiar contraseña', msg, 'warning');
        return;
      }
      if (newPassword.length < 5) {
        const msg = 'LA NUEVA CONTRASEÑA DEBE TENER MÍNIMO 5 CARACTERES';
        setPasswordError(msg);
        notify('Error al cambiar contraseña', msg, 'warning');
        return;
      }

      try {
        setIsChangingPassword(true);
        const Cookies = (await import('universal-cookie')).default;
        const cookies = new Cookies();
        const userCookie = cookies.get('101Obex');
        if (!userCookie || !userCookie.token) {
          const msg = 'NO SE ENCONTRÓ SESIÓN ACTIVA';
          setPasswordError(msg);
          notify('Error', msg, 'urgent');
          return;
        }

        const ObexRequestsHandlerP80 = (await import('../../handlers/ObexRequestsHandlerP80')).default;
        let res = await ObexRequestsHandlerP80.get('/auth-change-password', {
          access_token: userCookie.token,
          current_password: currentPassword,
          new_password: newPassword
        });
        if (res.data) res = res.data;

        // Note: res.status is always true regardless of business outcome on
        // this backend — only an explicit "exitosamente" message means success.
        const pwdResp = res?.response ?? res?.result?.response;
        if (typeof pwdResp === 'string' && pwdResp.toLowerCase().includes('exitosamente')) {
          setPasswordSuccess('CONTRASEÑA ACTUALIZADA EXITOSAMENTE');
          notify('Contraseña Actualizada', 'Tu contraseña ha sido modificada exitosamente.', 'info');
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
        } else {
          const errorMsg = typeof pwdResp === 'string' ? pwdResp.toUpperCase() : 'ERROR AL CAMBIAR CONTRASEÑA';
          setPasswordError(errorMsg);
          notify('Error al cambiar contraseña', errorMsg, 'urgent');
          return;
        }
      } catch (err) {
        console.error('Error changing password:', err);
        const errMsg = (err as any)?.response?.data?.response || (err as any)?.message || 'ERROR AL CAMBIAR CONTRASEÑA';
        const formattedErr = String(errMsg).toUpperCase();
        setPasswordError(formattedErr);
        notify('Error al cambiar contraseña', formattedErr, 'urgent');
        return;
      } finally {
        setIsChangingPassword(false);
      }
    }

    // Persist the extended profile fields (Datos_Personales/Laborales/
    // Bancarios/Notas) to the profiles table via /profile-uptade.
    try {
      const Cookies = (await import('universal-cookie')).default;
      const cookies = new Cookies();
      const userCookie = cookies.get('101Obex');
      if (!userCookie || !userCookie.token) {
        notify('Error', 'No se encontró sesión activa.', 'warning');
        return;
      }

      const profileParams: Record<string, any> = { access_token: userCookie.token };
      const fieldMap: [keyof ExtendedUserData, string][] = [
        ['dni', 'dni'],
        ['phone', 'phone'],
        ['birthDate', 'birth_date'],
        ['address', 'address'],
        ['employeeId', 'employee_code'],
        ['position', 'job_title'],
        ['hireDate', 'hire_date'],
        ['hourlyCost', 'hourly_cost'],
        ['iban', 'iban'],
        ['emergencyContactName', 'emergency_contact_name'],
        ['emergencyContactPhone', 'emergency_contact_phone'],
        ['notes', 'notes'],
        ['photoUrl', 'avatar_base64'],
      ];
      fieldMap.forEach(([field, param]) => {
        const value = formData[field];
        if (value !== undefined && value !== null && value !== '') {
          profileParams[param] = value;
        }
      });

      const ObexRequestsHandlerP80 = (await import('../../handlers/ObexRequestsHandlerP80')).default;
      let res = await ObexRequestsHandlerP80.get('/profile-uptade', profileParams);
      if (res.data) res = res.data;

      const profileResp = res?.response ?? res?.result?.response;
      if (!profileResp || typeof profileResp !== 'object' || !profileResp.id) {
        notify('Error', 'Error guardando la ficha: ' + (typeof profileResp === 'string' ? profileResp : 'Error desconocido'), 'warning');
        return;
      }
    } catch (err) {
      console.error('Error saving profile:', err);
      notify('Error', 'Error de red al guardar la ficha.', 'urgent');
      return;
    }

    const updatedUser: ExtendedUserData = {
      ...user,
      ...formData,
      ...(showPasswordChange && newPassword ? { password: newPassword } : {})
    } as ExtendedUserData;

    onSave(updatedUser);
    onClose();
  };

  return (
    <div className="epm-overlay">
      <div className="epm-backdrop" onClick={onClose} />

      <div className="epm-modal">
        {/* Neon corner lines */}
        <div className="epm-neon-corner epm-neon-top-left-h" />
        <div className="epm-neon-corner epm-neon-top-left-v" />
        <div className="epm-neon-corner epm-neon-top-right-h" />
        <div className="epm-neon-corner epm-neon-top-right-v" />

        {/* Header */}
        <div className="epm-header">
          <div className="epm-header-info">
            <div className="epm-pulse-dot" />
            <div>
              <h3 className="epm-header-title">
                <UserSquare2 size={14} style={{ color: 'var(--color-brand-green)' }} />
                Expediente_Maestro_Empleado
              </h3>
              <span className="epm-header-subtitle">
                ID UNIFORMADO: {user.id} • ÚLTIMA CONEXIÓN: {new Date(user.lastLogin).toLocaleString()}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="epm-close-btn">
            <X size={18} />
          </button>
        </div>

        {/* Body Split */}
        <div className="epm-body">
          {/* Sidebar with Photo & Status */}
          <div className="epm-sidebar">
            {/* Photo Area */}
            <div className="epm-photo-area">
              <div className="epm-photo-circle">
                {formData.photoUrl ? (
                  <img
                    src={formData.photoUrl}
                    alt={formData.name}
                    referrerPolicy="no-referrer"
                    className="epm-photo-img"
                  />
                ) : (
                  <User size={48} className="epm-photo-placeholder" />
                )}
              </div>
              <button
                type="button"
                onClick={() => setShowAvatarSelector(!showAvatarSelector)}
                className="epm-change-photo-btn"
                title="Cambiar foto de perfil"
              >
                <Camera size={14} />
              </button>
            </div>

            {/* Quick Status / Identity */}
            <div className="epm-quick-status">
              <span className="epm-quick-name">
                {formData.name || 'SIN NOMBRE'}
              </span>
              <span className="epm-quick-position">
                {formData.position || 'SIN PUESTO'}
              </span>
              <span className="epm-quick-email">
                {user.email}
              </span>
            </div>

            <div className="epm-divider" />

            {/* Status Indicators */}
            <div className="epm-status-indicators">
              <div className="epm-status-row">
                <div className={`epm-status-badge epm-status-${formData.status || 'active'}`}>
                  {formData.status === 'active' && <Check size={14} className="epm-status-icon" />}
                  {formData.status === 'inactive' && <X size={14} className="epm-status-icon" />}
                  {formData.status === 'locked' && <AlertOctagon size={14} className="epm-status-icon" />}
                  {formData.status === 'active' ? 'Activo' : formData.status === 'inactive' ? 'Inactivo' : 'Bloqueado'}
                </div>
              </div>
              <div className="epm-status-row">
                <span className="epm-status-label">Rol Heredado:</span>
                <span className="epm-status-value">{user.inheritedRoles?.join(', ') || 'SIN_ROL'}</span>
              </div>
              <div className="epm-status-row">
                <span className="epm-status-label">Grupo(s):</span>
                <span className="epm-status-value truncate" title={user.groupNames?.join(', ')}>
                  {user.groupNames && user.groupNames.length > 0 ? user.groupNames.join(', ') : 'NINGUNO'}
                </span>
              </div>
            </div>

            {/* Password change shortcut */}
            <button
              type="button"
              onClick={() => {
                setShowPasswordChange(!showPasswordChange);
                if (!showPasswordChange) setActiveTab('personal');
              }}
              className={`epm-password-toggle ${showPasswordChange ? 'active' : ''}`}
            >
              <Key size={10} />
              {showPasswordChange ? 'Cerrar_Clave' : 'Cambiar_Contraseña'}
            </button>
          </div>

          {/* Main Tabs Container */}
          <div className="epm-main-content">
            {/* Tab Selector bar */}
            <div className="epm-tabs">
              {[
                { id: 'personal', label: 'Datos_Personales', icon: User },
                { id: 'laboral', label: 'Datos_Laborales', icon: Briefcase },
                { id: 'financial', label: 'Datos_Bancarios', icon: CreditCard },
                { id: 'notes', label: 'Notas_Emergencia', icon: FileText }
              ].map(tab => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id && !showPasswordChange;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    disabled={showPasswordChange}
                    onClick={() => {
                      setActiveTab(tab.id as ActiveTab);
                      setShowAvatarSelector(false);
                    }}
                    className={`epm-tab-btn ${isActive ? 'active' : ''}`}
                  >
                    <Icon size={12} className="epm-tab-icon" />
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Form panel scrolling */}
            <form onSubmit={handleSave} className="epm-form-panel">
              {showPasswordChange && (
                <div className="epm-password-section">
                  <div className="epm-alert-box">
                    <AlertOctagon size={16} className="epm-alert-icon" />
                    <div>
                      <span className="epm-alert-title">Seguridad: Cambio de Credenciales</span>
                      <p className="epm-alert-text">
                        Para modificar la contraseña de acceso de este empleado, digite el nuevo código. Se requiere validación de coincidencia.
                      </p>
                    </div>
                  </div>

                  <div className="epm-grid epm-grid-3">
                    <div className="epm-form-group">
                      <label className="epm-label">Contraseña Actual</label>
                      <div className="epm-input-wrapper">
                        <Key className="epm-input-icon" size={12} />
                        <input
                          type="password"
                          required
                          value={currentPassword}
                          onChange={(e) => {
                            setCurrentPassword(e.target.value);
                            setPasswordError('');
                            setPasswordSuccess('');
                          }}
                          className="epm-input"
                          placeholder="••••••••••••"
                        />
                      </div>
                    </div>
                    <div className="epm-form-group">
                      <label className="epm-label">Nueva Contraseña</label>
                      <div className="epm-input-wrapper">
                        <Lock className="epm-input-icon" size={12} />
                        <input
                          type="password"
                          required
                          value={newPassword}
                          onChange={(e) => {
                            setNewPassword(e.target.value);
                            setPasswordError('');
                            setPasswordSuccess('');
                          }}
                          className="epm-input"
                          placeholder="••••••••••••"
                        />
                      </div>
                    </div>
                    <div className="epm-form-group">
                      <label className="epm-label">Confirmar Contraseña</label>
                      <div className="epm-input-wrapper">
                        <Check className="epm-input-icon" size={12} />
                        <input
                          type="password"
                          required
                          value={confirmPassword}
                          onChange={(e) => {
                            setConfirmPassword(e.target.value);
                            setPasswordError('');
                            setPasswordSuccess('');
                          }}
                          className="epm-input"
                          placeholder="••••••••••••"
                        />
                      </div>
                    </div>
                  </div>

                  {passwordError && (
                    <span className="epm-error-text">{passwordError}</span>
                  )}
                  {passwordSuccess && (
                    <span className="epm-success-text">{passwordSuccess}</span>
                  )}
                </div>
              )}

              {showAvatarSelector && !showPasswordChange && (
                <div className="epm-password-section">
                  <div className="epm-section-title">
                    <span>Galería de Fotos del Personal</span>
                  </div>

                  <div className="epm-avatar-gallery">
                    {PRESET_AVATARS.map((avatar, idx) => (
                      <div
                        key={idx}
                        onClick={() => {
                          handleInputChange('photoUrl', avatar);
                          setShowAvatarSelector(false);
                        }}
                        className={`epm-avatar-item ${formData.photoUrl === avatar ? 'selected' : ''}`}
                      >
                        <img
                          src={avatar}
                          alt={`Avatar ${idx + 1}`}
                          referrerPolicy="no-referrer"
                          className="epm-avatar-img"
                        />
                        {formData.photoUrl === avatar && (
                          <div className="epm-avatar-selected-overlay">
                            <Check style={{ color: 'var(--color-brand-green)' }} size={20} />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="epm-form-group">
                    <label className="epm-label">O ingresar URL personalizada de imagen</label>
                    <input
                      type="text"
                      value={formData.photoUrl || ''}
                      onChange={(e) => handleInputChange('photoUrl', e.target.value)}
                      className="epm-input"
                      placeholder="https://ejemplo.com/mi-foto.jpg"
                    />
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowAvatarSelector(false)}
                    className="epm-btn-secondary"
                  >
                    Volver
                  </button>
                </div>
              )}

              {!showPasswordChange && !showAvatarSelector && (
                <>
                  {activeTab === 'personal' && (
                    <div className="epm-password-section">
                      <div className="epm-section-title">
                        <User size={12} style={{ color: 'var(--color-brand-green)' }} />
                        <span>Información_Personal_Contacto</span>
                      </div>

                      <div className="epm-grid epm-grid-2">
                        <div className="epm-form-group">
                          <label className="epm-label">Nombre Completo del Empleado</label>
                          <input
                            type="text"
                            required
                            value={formData.name || ''}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            className="epm-input uppercase"
                            placeholder="EJ. RAÚL NOGALES"
                          />
                        </div>

                        <div className="epm-form-group">
                          <label className="epm-label">
                            Email Corporativo
                            <span className="epm-label-badge">Bloqueado</span>
                          </label>
                          <div className="epm-input-wrapper">
                            <Mail className="epm-input-icon" size={12} />
                            <input
                              type="email"
                              disabled
                              value={formData.email || ''}
                              className="epm-input uppercase"
                              placeholder="raul.nogales@101obex.com"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="epm-grid epm-grid-2">
                        <div className="epm-form-group">
                          <label className="epm-label">Documento de Identidad (DNI / NIE)</label>
                          <div className="epm-input-wrapper">
                            <UserSquare2 className="epm-input-icon" size={12} />
                            <input
                              type="text"
                              value={formData.dni || ''}
                              onChange={(e) => handleInputChange('dni', e.target.value)}
                              className="epm-input uppercase"
                              placeholder="EJ. 12345678X"
                            />
                          </div>
                        </div>

                        <div className="epm-form-group">
                          <label className="epm-label">Teléfono de Contacto</label>
                          <div className="epm-input-wrapper">
                            <Phone className="epm-input-icon" size={12} />
                            <input
                              type="text"
                              value={formData.phone || ''}
                              onChange={(e) => handleInputChange('phone', e.target.value)}
                              className="epm-input"
                              placeholder="EJ. +34 612 345 678"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="epm-grid epm-grid-2">
                        <div className="epm-form-group">
                          <label className="epm-label">Fecha de Nacimiento</label>
                          <div className="epm-input-wrapper">
                            <Calendar className="epm-input-icon" size={12} />
                            <input
                              type="date"
                              value={formData.birthDate || ''}
                              onChange={(e) => handleInputChange('birthDate', e.target.value)}
                              className="epm-input"
                            />
                          </div>
                        </div>

                        <div className="epm-form-group">
                          <label className="epm-label">Dirección de Residencia</label>
                          <div className="epm-input-wrapper">
                            <MapPin className="epm-input-icon" size={12} />
                            <input
                              type="text"
                              value={formData.address || ''}
                              onChange={(e) => handleInputChange('address', e.target.value)}
                              className="epm-input uppercase"
                              placeholder="EJ. CALLE MAYOR 12, 3ºB, MADRID"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'laboral' && (
                    <div className="epm-password-section">
                      <div className="epm-section-title">
                        <Briefcase size={12} style={{ color: 'var(--color-brand-green)' }} />
                        <span>Detalles_Laborales_Contratación</span>
                      </div>

                      <div className="epm-grid epm-grid-3">
                        <div className="epm-form-group">
                          <label className="epm-label">Código de Empleado (Ficha)</label>
                          <div className="epm-input-wrapper">
                            <UserSquare2 className="epm-input-icon" size={12} />
                            <input
                              type="text"
                              value={formData.employeeId || ''}
                              onChange={(e) => handleInputChange('employeeId', e.target.value)}
                              className="epm-input uppercase"
                              placeholder="EJ. EMP-001"
                            />
                          </div>
                        </div>

                        <div className="epm-form-group">
                          <label className="epm-label">Cargo / Puesto de Trabajo</label>
                          <div className="epm-input-wrapper">
                            <Briefcase className="epm-input-icon" size={12} />
                            <input
                              type="text"
                              value={formData.position || ''}
                              onChange={(e) => handleInputChange('position', e.target.value)}
                              className="epm-input uppercase"
                              placeholder="EJ. DIRECTOR DE SISTEMAS"
                            />
                          </div>
                        </div>

                        <div className="epm-form-group">
                          <label className="epm-label">Departamento de Control</label>
                          <div className="epm-input-wrapper">
                            <Layers className="epm-input-icon" size={12} />
                            <input
                              type="text"
                              value={formData.department || ''}
                              onChange={(e) => handleInputChange('department', e.target.value)}
                              className="epm-input uppercase"
                              placeholder="EJ. IT"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="epm-grid epm-grid-3">
                        <div className="epm-form-group">
                          <label className="epm-label">Fecha de Incorporación</label>
                          <div className="epm-input-wrapper">
                            <Calendar className="epm-input-icon" size={12} />
                            <input
                              type="date"
                              value={formData.hireDate || ''}
                              onChange={(e) => handleInputChange('hireDate', e.target.value)}
                              className="epm-input"
                            />
                          </div>
                        </div>

                        <div className="epm-form-group">
                          <label className="epm-label">Rol Heredado (de sus grupos)</label>
                          <div className="epm-input-wrapper">
                            <Shield className="epm-input-icon" size={12} />
                            <input
                              type="text"
                              disabled
                              readOnly
                              value={user.inheritedRoles?.join(', ') || 'Sin rol (sin grupo asignado)'}
                              className="epm-input"
                              title="El rol se hereda de los grupos a los que pertenece; gestiónalo desde Grupos"
                            />
                          </div>
                        </div>

                        <div className="epm-form-group">
                          <label className="epm-label">Grupo(s) de Trabajo</label>
                          <div className="epm-input-wrapper">
                            <Layers className="epm-input-icon" size={12} />
                            <input
                              type="text"
                              disabled
                              readOnly
                              value={user.groupNames?.join(', ') || 'Sin grupo asignado'}
                              className="epm-input"
                              style={{ fontWeight: 700, color: 'var(--color-brand-green)' }}
                              title="La pertenencia a grupos se gestiona desde la vista Grupos"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'financial' && (
                    <div className="epm-password-section">
                      <div className="epm-grid epm-grid-2">
                        <div className="epm-form-group">
                          <label className="epm-label">Coste / Tarifa por Hora (€)</label>
                          <div className="epm-input-wrapper">
                            <DollarSign className="epm-input-icon" size={12} />
                            <input
                              type="number"
                              value={formData.hourlyCost || ''}
                              onChange={(e) => handleInputChange('hourlyCost', Number(e.target.value))}
                              className="epm-input"
                              placeholder="EJ. 35.50"
                              step="0.01"
                            />
                          </div>
                        </div>

                        <div className="epm-form-group">
                          <label className="epm-label">Cuenta Bancaria (IBAN para Nóminas)</label>
                          <div className="epm-input-wrapper">
                            <CreditCard className="epm-input-icon" size={12} />
                            <input
                              type="text"
                              value={formData.iban || ''}
                              onChange={(e) => handleInputChange('iban', e.target.value)}
                              className="epm-input uppercase"
                              placeholder="ES21 1465 0100 99 1234567890"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="epm-info-box">
                        <span>AUDITORÍA Y FACTURACIÓN</span>
                        <p>
                          La tarifa horaria ingresada se emplea en los cálculos automáticos de coste de tareas de flujos editoriales y monitorización logística por Albatross ERP.
                        </p>
                      </div>
                    </div>
                  )}

                  {activeTab === 'notes' && (
                    <div className="epm-password-section">
                      <div className="epm-section-title">
                        <span>Contacto de Emergencia</span>
                      </div>

                      <div className="epm-grid epm-grid-2">
                        <div className="epm-form-group">
                          <label className="epm-label">Nombre del Contacto</label>
                          <div className="epm-input-wrapper">
                            <User className="epm-input-icon" size={12} />
                            <input
                              type="text"
                              value={formData.emergencyContactName || ''}
                              onChange={(e) => handleInputChange('emergencyContactName', e.target.value)}
                              className="epm-input uppercase"
                              placeholder="EJ. MARÍA NOGALES (HERMANA)"
                            />
                          </div>
                        </div>

                        <div className="epm-form-group">
                          <label className="epm-label">Teléfono de Emergencia</label>
                          <div className="epm-input-wrapper">
                            <Phone className="epm-input-icon" size={12} />
                            <input
                              type="text"
                              value={formData.emergencyContactPhone || ''}
                              onChange={(e) => handleInputChange('emergencyContactPhone', e.target.value)}
                              className="epm-input"
                              placeholder="EJ. +34 689 777 555"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="epm-form-group">
                        <label className="epm-label">Notas del Expediente / Observaciones</label>
                        <textarea
                          value={formData.notes || ''}
                          onChange={(e) => handleInputChange('notes', e.target.value)}
                          className="epm-textarea"
                          placeholder="Ingrese notas adicionales de contratación, aptitudes, permisos especiales..."
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
            </form>

            <div className="epm-footer">
              <span className="epm-footer-text">
                Marcial Pons • Ficha Expediente Empleado
              </span>
              <div className="epm-footer-actions">
                <button
                  type="button"
                  onClick={onClose}
                  className="epm-btn-cancel"
                >
                  Cerrar
                </button>
                <button
                  type="submit"
                  onClick={handleSave}
                  className="epm-btn-save"
                >
                  <Check size={12} />
                  Guardar_Ficha
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
